import { Text, View } from 'react-native'
import { useForm, type SubmitHandler } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { COUNTRY_CODES } from '@/constants/config'
import { Button } from '@/components/base'
import { FormInput, OtpInput, PhoneInput } from '@/components/form-control'
import { useLoadingIndicator } from '@/components/LoadingIndicator'

export type ForgotValues = {
  phoneNumber: {
    code: string
    number: string
  }
  verifyCode: string
  newPassword: string
  newPasswordConfirm: string
}

const defaultValues: ForgotValues = {
  phoneNumber: {
    code: COUNTRY_CODES.China.dial_code,
    number: ''
  },
  verifyCode: '',
  newPassword: '',
  newPasswordConfirm: ''
}

interface ForgotFormProps {
  step: 'verify' | 'reset'
  onVerify: SubmitHandler<ForgotValues>
  onSubmit: SubmitHandler<ForgotValues>
  requestOTP: (code: string, number: string) => Promise<void>
}

// TODO, prototype, need to implement
export function ForgotForm({ step, onVerify, onSubmit, requestOTP }: ForgotFormProps) {
  const { control, handleSubmit, setFocus, watch } = useForm<ForgotValues>({ defaultValues })
  const { t } = useTranslation()
  const loading = useLoadingIndicator()
  const phoneNumber = watch('phoneNumber')
  const verifyCode = watch('verifyCode')
  const newPassword = watch('newPassword')
  const newPasswordConfirm = watch('newPasswordConfirm')

  const request = async () => {
    await requestOTP(phoneNumber.code, phoneNumber.number)
  }

  switch (step) {
    case 'verify':
      return (
        <View className={'gap-y-4 px-4'}>
          <View className={'justify-center pb-3 pt-2'}>
            <Text className={'text-2xl font-semibold text-text-primary'}>{t('auth:retrievePassword')}</Text>
          </View>
          <View className={'gap-y-2'}>
            <PhoneInput
              control={control}
              name={{ code: 'phoneNumber.code', number: 'phoneNumber.number' }}
              label={t('auth:label:phoneNumber')}
              placeholder={t('auth:placeholder.phoneNumber')}
              textContentType={'none'}
              autoCorrect={false}
              returnKeyType={'next'}
              onSubmitEditing={() => setFocus('verifyCode')}
            />
            <OtpInput control={control} onSubmit={handleSubmit(onVerify)} requestOTP={request} />
          </View>
          <Text className={'h-[44px] text-sm text-text-secondary'}>{t('auth:otpDeliveryNotice')}</Text>
          <Button
            onPress={handleSubmit(onVerify)}
            disabled={phoneNumber.number === '' || verifyCode === '' || loading.isLoading}
          >
            {t('auth:continue')}
          </Button>
        </View>
      )
    case 'reset':
      return (
        <View className={'gap-y-4 px-4'}>
          <View className={'mt-4 gap-y-2'}>
            <FormInput
              control={control}
              name={'newPassword'}
              label={t('auth:label.newPassword')}
              hint={t('auth:hint.password')}
              type={'password'}
              placeholder={t('auth:placeholder.newPassword')}
              textContentType={'newPassword'}
              autoCapitalize={'none'}
              autoCorrect={false}
              returnKeyType={'next'}
              onSubmitEditing={() => setFocus('newPasswordConfirm')}
            />
            <FormInput
              control={control}
              name={'newPasswordConfirm'}
              label={t('auth:label.confirmNewPassword')}
              type={'password'}
              placeholder={t('auth:placeholder.confirmNewPassword')}
              textContentType={'newPassword'}
              autoCapitalize={'none'}
              autoCorrect={false}
              returnKeyType={'go'}
              onSubmitEditing={handleSubmit(onSubmit)}
            />
          </View>
          <Button
            onPress={handleSubmit(onSubmit)}
            disabled={newPassword === '' || newPasswordConfirm === '' || loading.isLoading}
          >
            {t('auth:complete')}
          </Button>
        </View>
      )
    default:
      return null
  }
}
